/**
 * „Podgląd u klienta" — the trainer's own colour, on a client's home screen.
 *
 * This is the whole point of the tab beside it: the colour is not the trainer's app, it is their
 * clients', and a swatch on a tile does not tell anybody what a progress bar or a start button will
 * look like. Dragging the picker repaints this on the same frame, because both read one value.
 *
 * ── The screen is real HTML over a photograph of a phone ────────────────────────────────────
 * The frame is a PNG and everything inside it is markup, positioned over the frame's display area
 * with the mockup's own percentages and sized in `cqw` so the whole thing scales with the column.
 * A second screenshot would have to be re-rendered for every colour, which is what makes this a
 * preview rather than an illustration.
 *
 * ── It is DECORATION to a screen reader, and says so in one sentence ────────────────────────
 * `aria-hidden` over the drawing, plus one visually-hidden line describing what it is. Reading out
 * a fake client's fake week is noise; knowing that the trainer's colour is being previewed on a
 * client's phone is the fact. The frame itself is `alt=""` — it is a picture of a phone.
 *
 * ── The names are the trainer's real ones ───────────────────────────────────────────────────
 * From the session, not invented: the point is recognising yourself in your client's app. The
 * client's own name is the only fiction here, and the mockup's.
 */

import { useTranslation } from 'react-i18next';

import { useSessionUser } from '~/entities/session';
import { displayInitials } from '~/shared/lib/displayInitials';
import { EyeIcon } from '~/shared/ui/AuthIcons';
import { PhoneFrame } from '~/shared/ui/PhoneFrame';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountBrandColorApi } from '../lib/useBrandColor';
import styles from './PhonePreview.module.css';

interface PhonePreviewProps {
  readonly brand: AccountBrandColorApi;
}

/** The mockup's own week: two done, one due on Thursday, the rest to come. Keyed 1–7, which is how
 * `web:weekdays.short` is keyed and how the backend numbers a day. */
const WEEK = [
  { day: 1, state: 'done' },
  { day: 2, state: 'done' },
  { day: 3, state: 'idle' },
  { day: 4, state: 'next' },
  { day: 5, state: 'idle' },
  { day: 6, state: 'idle' },
  { day: 7, state: 'idle' },
] as const;

/** What the phone wears while the read is open — the skeleton's own neutral, never Flame. */
const NEUTRAL = {
  fill: 'var(--skeleton-fill)',
  ink: 'var(--text-2)',
  solid: 'var(--skeleton-fill)',
  markInk: 'var(--text-2)',
} as const;

export function PhonePreview({ brand }: PhonePreviewProps) {
  const { t } = useTranslation();
  const user = useSessionUser();

  const name = user?.displayName ?? '';

  /**
   * While the read is open the phone is drawn in the SKELETON's own neutral rather than in Flame.
   *
   * Flame is what a trainer who has never chosen gets, so painting it during the wait would be the
   * app asserting an answer it does not have yet — and a phone that turns from orange to teal when
   * the answer lands is the colour version of the jump this app does not do. Neutral says „not yet",
   * and `reveal` hands the drawing over when it arrives.
   */
  const paint = brand.skeleton ? NEUTRAL : brand.paint;
  const brandVariables = {
    '--brand-fill': paint.fill,
    '--brand-ink': paint.ink,
    '--brand-solid': paint.solid,
    '--brand-mark': paint.markInk,
  };

  return (
    <div className={styles.column}>
      <p className={styles.caption}>
        <EyeIcon className={styles.captionIcon} />
        {t('profile.brandColor.previewEyebrow')}
      </p>

      {/* One sentence for a listener, in place of the drawing. */}
      <span className={hidden.srOnly}>{t('web:account.brandColor.previewAlt')}</span>

      <PhoneFrame className={styles.phoneSize} style={brandVariables as React.CSSProperties}>
        {/* The box never changes: every row below is laid out identically in all three states and
         * only the four `--brand-*` values differ, so the answer landing repaints and cannot
         * reflow. `reveal` is the gate's hand-over, and it is empty unless a wait was drawn. */}
        <div className={`${styles.body} ${String(brand.reveal)}`}>
          <p className={styles.hello}>
            {t('web:account.brandColor.preview.hello')}
            <b className={styles.today}>{t('web:account.brandColor.preview.today')}</b>
          </p>

          <div className={styles.card}>
            <small className={styles.eyebrow}>{t('web:account.brandColor.preview.week')}</small>
            <b className={styles.cardTitle}>{t('web:account.brandColor.preview.planName')}</b>
            <span className={styles.track}>
              <i className={styles.fillBar} />
            </span>
            <p className={styles.meta}>
              <span>{t('web:account.brandColor.preview.progress')}</span>
              <span>62%</span>
            </p>
            <span className={styles.cta}>{t('profile.brandColor.previewStart')}</span>
          </div>

          <div className={styles.card}>
            <div className={styles.coach}>
              <span className={styles.avatar}>{displayInitials(name)}</span>
              <span className={styles.coachText}>
                <b className={styles.coachName}>{name}</b>
                {t('web:account.brandColor.preview.yourTrainer')}
              </span>
              <span className={styles.message}>{t('web:account.brandColor.preview.message')}</span>
            </div>
          </div>

          <div className={styles.card}>
            <small className={styles.eyebrow}>{t('web:account.brandColor.preview.thisWeek')}</small>
            <div className={styles.week}>
              {WEEK.map((day) => (
                <i
                  key={day.day}
                  className={`${String(styles.day)} ${
                    day.state === 'done'
                      ? String(styles.dayDone)
                      : day.state === 'next'
                        ? String(styles.dayNext)
                        : ''
                  }`}
                >
                  {t(`web:weekdays.short.${String(day.day)}`)}
                </i>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <small className={styles.eyebrow}>{t('web:account.brandColor.preview.nextSlot')}</small>
            <b className={`${String(styles.cardTitle)} ${String(styles.cardTitleLast)}`}>
              {t('web:account.brandColor.preview.nextSlotValue')}
            </b>
          </div>

          <div className={styles.tabs}>
            <i className={`${String(styles.tabMark)} ${String(styles.tabMarkOn)}`} />
            <i className={styles.tabMark} />
            <i className={styles.tabMark} />
            <i className={styles.tabMark} />
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}
